import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { bookings } from '@repletoryreach/sdk-api';
import { Badge, Button, Group, Stack, Text } from '@repletoryreach/sdk-ui';
import { Alert, DataTable, EmptyState, PageHeader, StatusBadge, TableShell } from '@repletoryreach/sdk-ui/data-display';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { Switch } from '@repletoryreach/sdk-ui/inputs';
import { notifyError, notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { HelpButton } from '../../../app/guides/HelpButton';
import { useT } from '../../../i18n/I18nProvider';
import type { Indexed } from '../../../lib/types';
import { useIsOrgAdmin } from '../../../app/components/RoleGate';
import { BOOKINGS_KEY, bookingErrorText, serviceColour, useBookingSettings, useServices, useStaff } from '../lib/data';
import { ServiceDrawer } from './ServiceDrawer';
import '../bookings.css';

export default function BookingServicesPage() {
  const t = useT();
  const nav = useNavigate();
  const qc = useQueryClient();
  const canEdit = useIsOrgAdmin();
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<bookings.Service | 'new' | null>(null);
  const servicesQ = useServices(true);
  const staffQ = useStaff();
  const settingsQ = useBookingSettings();
  const all = servicesQ.data?.data ?? [];
  const rows = (showInactive ? all : all.filter((s) => s.active)) as Indexed<bookings.Service>[];
  const staff = staffQ.data?.data ?? [];
  const limit = settingsQ.data?.data.limits?.booking_services ?? null;
  const activeCount = all.filter((s) => s.active).length;
  const atLimit = limit !== null && activeCount >= limit;

  const refresh = () => qc.invalidateQueries({ queryKey: [BOOKINGS_KEY] });
  const remove = useMutation({
    mutationFn: (s: bookings.Service) => bookings.deleteService(s.id),
    onSuccess: (_r, s) => { void refresh(); notifySuccess(t('bookings.services.toast.removed', { name: s.name }), t('bookings.services.title')); },
    onError: (e) => notifyError(bookingErrorText(t, e), t('bookings.services.title'))
  });

  function confirmRemove(s: bookings.Service) {
    openConfirmModal({
      title: t('bookings.services.remove.title', { name: s.name }),
      children: <Text role="bodySmall">{t('bookings.services.remove.body')}</Text>,
      labels: { confirm: t('action.remove'), cancel: t('action.back') },
      confirmProps: { color: 'red' },
      onConfirm: () => remove.mutate(s)
    });
  }

  const staffNames = (ids: string[]) => staff.filter((p) => ids.includes(p.id)).map((p) => p.name);

  return (
    <Stack gap="lg">
      <PageHeader
        title={t('bookings.services.title')}
        description={t('bookings.services.description')}
        withDivider={false}
        actions={(
          <Group gap="sm">
            <HelpButton guide="bookings" />
            {canEdit && <Button size="sm" data-guide="bookings-services-add" disabled={atLimit} onClick={() => setEditing('new')}>{t('bookings.services.new')}</Button>}
          </Group>
        )}
      />

      {!canEdit && <Alert color="info">{t('bookings.readOnly')}</Alert>}
      {canEdit && atLimit && (
        <Alert color="warning">
          <Group gap="sm" justify="space-between" wrap="wrap">
            <span>{t('bookings.services.limitReached', { limit })}</span>
            <Button size="xs" variant="default" onClick={() => nav('/billing')}>{t('bookings.limit.seePlans')}</Button>
          </Group>
        </Alert>
      )}

      <TableShell
        data-guide="bookings-services-list"
        totalLabel={limit !== null ? t('bookings.services.countOfLimit', { count: activeCount, limit }) : undefined}
        actions={<Switch size="sm" label={t('bookings.services.showInactive')} checked={showInactive} onChange={setShowInactive} />}
        loading={servicesQ.isPending}
        error={servicesQ.error ? (
          <Alert color="error" role="alert">
            <Group gap="sm" justify="space-between" wrap="wrap">
              <span>{bookingErrorText(t, servicesQ.error)}</span>
              <Button size="xs" variant="default" onClick={() => void servicesQ.refetch()}>{t('action.retry')}</Button>
            </Group>
          </Alert>
        ) : undefined}
        isEmpty={!servicesQ.isPending && rows.length === 0}
        empty={(
          <EmptyState
            title={all.length > 0 ? t('bookings.services.noneActive') : t('bookings.services.emptyTitle')}
            description={all.length > 0 ? t('bookings.services.noneActiveHint') : t('bookings.services.emptyHint')}
            action={canEdit && all.length === 0 ? <Button size="sm" onClick={() => setEditing('new')}>{t('bookings.services.addFirst')}</Button>
              : all.length > 0 ? <Button size="xs" variant="default" onClick={() => setShowInactive(true)}>{t('bookings.services.showInactive')}</Button> : undefined}
          />
        )}
      >
        <DataTable<Indexed<bookings.Service>>
          records={rows}
          idAccessor="id"
          onRowClick={(s) => setEditing(s)}
          columns={[
            {
              accessor: 'name',
              title: t('common.name'),
              render: (s) => (
                <Group gap={8} wrap="nowrap">
                  <span className="rr-bookings__swatch" style={{ background: serviceColour(s.colour) }} aria-hidden />
                  <Stack gap={0}>
                    <Text role="bodySmall" fw={600}>{s.name}</Text>
                    {s.price_label && <Text role="caption" c="var(--rr-color-text-muted)">{s.price_label}</Text>}
                  </Stack>
                </Group>
              )
            },
            { accessor: 'kind', title: t('bookings.services.kind'), render: (s) => <Badge size="xs" variant="light">{t(`bookings.kind.${s.kind}`)}</Badge> },
            {
              accessor: 'duration_minutes',
              title: t('bookings.services.duration'),
              render: (s) => <Text role="bodySmall">{s.kind === 'callout' ? t('bookings.services.windowsCount', { count: s.callout_windows.length }) : t('bookings.services.minutes', { n: s.duration_minutes })}</Text>
            },
            {
              accessor: 'staff_ids',
              title: t('bookings.services.people'),
              render: (s) => {
                if (s.kind === 'appointment' && s.staff_mode === 'none') {
                  return <Text role="caption">{t('bookings.services.noPersonAtOnce', { n: s.capacity ?? 1 })}</Text>;
                }
                if (s.kind !== 'appointment' && s.staff_ids.length === 0) return <Text role="caption" c="var(--rr-color-text-muted)">{t('common.none')}</Text>;
                const names = staffNames(s.staff_ids);
                return names.length === 0
                  ? <Text role="caption" c="var(--rr-color-warning)">{t('bookings.services.nobody')}</Text>
                  : <Text role="bodySmall" lineClamp={1} title={names.join(', ')}>{names.join(', ')}</Text>;
              }
            },
            {
              accessor: 'requires_approval',
              title: t('bookings.services.approval'),
              render: (s) => <Text role="caption">{t(s.requires_approval === true ? 'bookings.services.approval.always' : s.requires_approval === false ? 'bookings.services.approval.never' : 'bookings.services.approval.default')}</Text>
            },
            {
              accessor: 'active',
              title: t('common.status'),
              render: (s) => <StatusBadge status={s.active ? 'healthy' : 'inactive'}>{s.active ? t('bookings.services.on') : t('bookings.services.off')}</StatusBadge>
            },
            {
              accessor: 'actions',
              title: t('common.actions'),
              render: (s) => (
                <Group gap="xs" wrap="nowrap" onClick={(e) => e.stopPropagation()}>
                  <Button size="xs" variant="subtle" onClick={() => setEditing(s)}>{canEdit ? t('action.edit') : t('action.view')}</Button>
                  {canEdit && s.active && (
                    <Button size="xs" variant="subtle" color="red" loading={remove.isPending && remove.variables?.id === s.id} onClick={() => confirmRemove(s)}>
                      {t('action.remove')}
                    </Button>
                  )}
                </Group>
              )
            }
          ]}
        />
      </TableShell>

      <ServiceDrawer target={editing} staff={staff} canEdit={canEdit} onClose={() => setEditing(null)} onSaved={() => void refresh()} />
    </Stack>
  );
}
